import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Fieldset, Icon, Input } from '@fourthwall/components';
import { DragIcon, PlusIcon, TrashIcon } from '@fourthwall/icons';
import { isProduction } from '@fourthwall/utils';
import { CredentialDisplay } from '../BasicInformationTab/components/CredentialDisplay/CredentialDisplay';
import { useGetPlatformAppClientSecret } from '../BasicInformationTab/hooks/useGetPlatformAppClientSecret';
import * as S from './OAuthTab.styled';
import { useUpdatePlatformAppClientRedirectUris } from './hooks/useUpdatePlatformAppClientRedirectUris';
const validationSchema = Yup.object().shape({
    redirectUris: Yup.array().of(Yup.string()),
});
const getInitialValues = (app) => ({
    redirectUris: app?.redirectUris || [],
});
const SortableUriItem = ({ uri, isDefault, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: uri,
    });
    return (React.createElement(S.UriItem, { ref: setNodeRef, ...attributes, style: {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        } },
        React.createElement(S.UriContent, null,
            React.createElement(S.DragHandle, { ...listeners },
                React.createElement(Icon, { component: DragIcon, height: 12 })),
            React.createElement(S.UriText, null, uri),
            isDefault && React.createElement(S.DefaultBadge, null, "Default")),
        React.createElement(Button, { appearance: "secondary", size: "small", icon: React.createElement(Icon, { component: TrashIcon, height: 16 }), onClick: onRemove })));
};
export const OAuthTab = ({ app }) => {
    const { updatePlatformAppClientRedirectUris, isLoading } = useUpdatePlatformAppClientRedirectUris();
    const { getPlatformAppClientSecret } = useGetPlatformAppClientSecret();
    const handleRedirectUrisSave = (redirectUris) => {
        if (!app.id)
            return;
        updatePlatformAppClientRedirectUris([{ clientId: app.id }, { redirectUris }]);
    };
    const [newUri, setNewUri] = useState('');
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleSubmit = (values) => {
        handleRedirectUrisSave(values.redirectUris);
    };
    const handleAddUri = (setFieldValue, values) => {
        if (newUri.trim() && !values.redirectUris.includes(newUri.trim())) {
            const updatedUris = [...values.redirectUris, newUri.trim()];
            setFieldValue('redirectUris', updatedUris);
            setNewUri('');
        }
    };
    const handleRemoveUri = (setFieldValue, values, index) => {
        const updatedUris = values.redirectUris.filter((_, i) => i !== index);
        setFieldValue('redirectUris', updatedUris);
    };
    const handleDragEnd = (event, setFieldValue, values) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = values.redirectUris.indexOf(active.id);
            const newIndex = values.redirectUris.indexOf(over?.id);
            if (oldIndex !== -1 && newIndex !== -1) {
                const updatedUris = [...values.redirectUris];
                const [movedUri] = updatedUris.splice(oldIndex, 1);
                updatedUris.splice(newIndex, 0, movedUri);
                setFieldValue('redirectUris', updatedUris);
            }
        }
    };
    return (React.createElement(Formik, { initialValues: getInitialValues(app), validationSchema: validationSchema, onSubmit: handleSubmit }, ({ submitForm, setFieldValue, values }) => (React.createElement(Form, null,
        React.createElement(S.Container, null,
            React.createElement(S.CredentialsSection, null,
                React.createElement(Fieldset, null,
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(S.SectionLabel, null, "Credentials"))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(CredentialDisplay, { label: "Client ID", value: app.id, copyable: true }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(CredentialDisplay, { label: "Client Secret", copyable: true, onReveal: async () => {
                                    if (app.id) {
                                        const data = await getPlatformAppClientSecret([{ clientId: app.id }]);
                                        return data?.secret || '';
                                    }
                                    return '';
                                } }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(CredentialDisplay, { label: "Authorization URL", value: `https://my-shop.${isProduction() ? '' : 'staging.'}fourthwall.com/admin/platform-apps/${app.id}/connect?redirect_uri=${encodeURIComponent(app.redirectUris?.[0] || '')}`, copyable: true }))))),
            React.createElement(S.SectionLabel, null, "Redirect URIs"),
            React.createElement(S.Description, null, "Configure the redirect URIs that your app will use for OAuth authentication. The first URI in the list will be used as the default."),
            React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: (event) => handleDragEnd(event, setFieldValue, values) },
                React.createElement(SortableContext, { items: values.redirectUris, strategy: verticalListSortingStrategy },
                    React.createElement(S.UrisContainer, null, values.redirectUris.map((uri, index) => (React.createElement(SortableUriItem, { key: uri, uri: uri, isDefault: index === 0, onRemove: () => handleRemoveUri(setFieldValue, values, index) })))))),
            React.createElement(S.AddUriContainer, null,
                React.createElement(Input, { value: newUri, onChange: (e) => setNewUri(e.target.value), placeholder: "Enter redirect URI (e.g., https://your-app.com/callback)", onKeyDown: (e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddUri(setFieldValue, values);
                        }
                    } }),
                React.createElement(Button, { label: "Add", icon: React.createElement(PlusIcon, null), onClick: () => handleAddUri(setFieldValue, values), disabled: !newUri.trim() })),
            React.createElement(S.ButtonContainer, null,
                React.createElement(Button, { label: "Save Changes", appearance: "primary", onClick: submitForm, disabled: isLoading, loading: isLoading })))))));
};
